import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Check } from 'lucide-react';
import { statusTone } from './VisaStatements';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/expenses/visa`;
const fmt = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtD = (d) => d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

// A cardholder's own Visa charges — nothing from anyone else's card is ever sent to this view.
export default function MyVisaCharges({ onOpenReport }) {
  const [tx, setTx] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/mine`); setTx(r.data.transactions || []); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const lost = async (t) => {
    if (!window.confirm(`No receipt for ${t.description} (${fmt(t.amount)})?\n\nA "Lost receipt" line for the full amount (HST $0) will be added to your open expense report — set the Job # there.`)) return;
    setBusy(t.id);
    try {
      const r = await axios.post(`${API}/statements/${t.statement_id}/tx/${t.id}/lost-receipt`, {});
      toast.success('Added to your expense report — set the Job #');
      await load();
      onOpenReport?.(r.data.report_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy(null);
  };

  const need = tx.filter((t) => !t.match?.status || t.match.status === 'unmatched').length;
  return (
    <div className="space-y-3" data-testid="my-visa-charges">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-700 flex items-center gap-2"><CreditCard className="w-4 h-4 text-blue-700" />Charges on your company Visa</p>
        <div className="flex items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1 text-emerald-700"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-400 inline-block" />expensed</span>
          <span className="inline-flex items-center gap-1 text-amber-700"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-400 inline-block" />needs an expense {need > 0 && <b>({need})</b>}</span>
        </div>
      </div>
      {tx.length === 0 && <p className="text-sm text-slate-500 text-center py-6" data-testid="my-visa-empty">No charges on your card yet.</p>}
      <div className="space-y-1.5">
        {tx.map((t) => {
          const tone = statusTone(t.match?.status);
          const open = !t.match?.status || t.match.status === 'unmatched';
          return (
            <div key={t.id} className={`rounded-lg border border-l-4 px-3 py-2 flex flex-wrap items-center gap-3 ${tone.row}`} data-testid={`my-visa-tx-${t.id}`} data-status={t.match?.status || 'unmatched'}>
              <span className="text-xs text-slate-700 w-14">{fmtD(t.trans_date)}</span>
              <span className="flex-1 min-w-[140px] text-sm text-slate-900 truncate" title={t.description}>{t.description}</span>
              <span className="text-sm font-mono text-slate-900 w-24 text-right">{fmt(t.amount)}</span>
              <span className={`text-[11px] px-2 py-0.5 rounded font-semibold ${tone.badge}`}>{tone.label}</span>
              {!open && t.match?.report_id && (
                <button onClick={() => onOpenReport?.(t.match.report_id)} className="text-xs text-emerald-800 hover:text-slate-900 underline-offset-2 hover:underline truncate max-w-[260px]" data-testid={`my-visa-open-${t.id}`}>{t.match.report_title}{t.match.note ? ` · ${t.match.note}` : ''}</button>
              )}
              {open && (
                <button disabled={busy === t.id} onClick={() => lost(t)} className="text-xs px-2 py-1 rounded border border-amber-300 text-amber-800 hover:bg-amber-50 inline-flex items-center gap-1" data-testid={`my-visa-lost-${t.id}`}>
                  <Check className="w-3 h-3" />{busy === t.id ? 'Adding…' : 'Lost receipt'}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-500">Amber charges clear automatically once you upload the receipt to an expense report as a Company Card line.</p>
    </div>
  );
}
